import { InternalAxiosRequestConfig } from 'axios';

import { isPublicPath } from '../lib/auth/publicPaths';
import { getAccessToken } from '../lib/auth/AuthContext';
import { isSignInAttempt } from '../lib/auth/signInAttempts';
import { clearViewerBinding } from '../lib/auth/viewerSession';
import { AxiosClient, type AxiosErrorHandlerMap } from '../lib/axios';

const errorHandlerMap: AxiosErrorHandlerMap = {
  401: (error) => {
    // A refused sign-in is not an expired session. The form that asked renders
    // its own "Invalid email or password", and everything below would destroy
    // it: `window.location.href` is a full page load, so the message is wiped
    // before it paints and the fields blank themselves. The user is told
    // nothing, which reads as the site being broken rather than a typo.
    //
    // Leave the stored tokens alone too. Somebody failing a sign-in should not
    // end another tab's session.
    if (isSignInAttempt(error?.config?.url)) {
      return;
    }

    clearViewerBinding();
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('selected_org');
    // A patient on a share or claim link has no staff session to recover, and
    // navigating away would throw out the token in their URL. Clearing the
    // stale staff credentials above is still right; the redirect is not.
    if (isPublicPath()) {
      return;
    }
    window.location.href = '/login';
  },
};

export const clientApi = AxiosClient.build(import.meta.env.VITE_API_URL).withMiddleware({
  errorHandlerMap,
  mutationTimeout: 40000,
  requestTransformer: async (request: InternalAxiosRequestConfig) => {
    const token = getAccessToken();
    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`);
    }
    request.headers.set('Content-Type', 'application/json');

    if (request.data instanceof FormData) {
      request.headers.delete('Content-Type');
    }
  },
});

export const publicApi = AxiosClient.build(import.meta.env.VITE_API_URL).withMiddleware({
  errorHandlerMap: {},
  mutationTimeout: 40000,
  requestTransformer: async (request: InternalAxiosRequestConfig) => {
    request.headers.set('Content-Type', 'application/json');
  },
});
